import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "../i18n/LanguageContext";
import { useAuth } from "../utils/auth.jsx";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";

export function usePersonaLibrary() {
  const { t, language } = useTranslation();

  // Library state
  const [libraryPersonas, setLibraryPersonas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [sortBy, setSortBy] = useState("updated_at");

  // Editor state
  const [editingPersona, setEditingPersona] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  // Panel state
  const [panels, setPanels] = useState([]);
  const [currentPanel, setCurrentPanel] = useState({ persona_ids: [] });
  const [representativeness, setRepresentativeness] = useState(null);

  // Benchmarks & archetypes
  const [benchmarks, setBenchmarks] = useState([]);
  const [archetypes, setArchetypes] = useState([]);
  const [selectedBenchmark, setSelectedBenchmark] = useState("sweden-2025");

  // AI state
  const [isAiWorking, setIsAiWorking] = useState(false);

  // Sub-view state: 'library' | 'editor' | 'panel-builder' | 'archetypes'
  const [subView, setSubView] = useState("library");

  const { token } = useAuth();

  // Fetch library personas
  const fetchPersonas = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set("search", searchQuery);
      if (tagFilter) params.set("tag", tagFilter);
      params.set("sort_by", sortBy);
      const res = await fetch(`${API_BASE}/library/personas?${params}`);
      if (res.ok) {
        const data = await res.json();
        setLibraryPersonas(data.personas || data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, tagFilter, sortBy, token]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  // Fetch benchmarks and archetypes on mount
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/library/benchmarks`)
      .then((r) => r.json())
      .then((d) => setBenchmarks(Array.isArray(d) ? d : d.benchmarks || []))
      .catch(() => {});
    fetch(`${API_BASE}/library/archetypes`)
      .then((r) => r.json())
      .then((d) => setArchetypes(Array.isArray(d) ? d : d.archetypes || []))
      .catch(() => {});
    fetch(`${API_BASE}/library/panels`)
      .then((r) => r.json())
      .then((d) => setPanels(Array.isArray(d) ? d : d.panels || []))
      .catch(() => {});
  }, [token]);

  // CRUD operations
  const createPersona = useCallback(
    async (data) => {
      const res = await fetch(`${API_BASE}/library/personas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        await fetchPersonas();
        return await res.json();
      }
      throw new Error("Failed to create persona");
    },
    [fetchPersonas],
  );

  const updatePersona = useCallback(
    async (id, data) => {
      const res = await fetch(`${API_BASE}/library/personas/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        await fetchPersonas();
        return await res.json();
      }
      throw new Error("Failed to update persona");
    },
    [fetchPersonas],
  );

  const deletePersona = useCallback(
    async (id) => {
      await fetch(`${API_BASE}/library/personas/${id}`, { method: "DELETE" });
      await fetchPersonas();
    },
    [fetchPersonas],
  );

  const duplicatePersona = useCallback(
    async (id) => {
      const res = await fetch(`${API_BASE}/library/personas/${id}/duplicate`, {
        method: "POST",
      });
      if (res.ok) {
        await fetchPersonas();
        return await res.json();
      }
    },
    [fetchPersonas],
  );

  // AI operations
  const enrichPersona = useCallback(
    async (id) => {
      setIsAiWorking(true);
      try {
        const res = await fetch(`${API_BASE}/library/personas/${id}/enrich`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ language }),
        });
        if (res.ok) {
          const updated = await res.json();
          await fetchPersonas();
          return updated;
        }
      } finally {
        setIsAiWorking(false);
      }
    },
    [language, fetchPersonas],
  );

  const regenerateStory = useCallback(
    async (id) => {
      setIsAiWorking(true);
      try {
        const res = await fetch(
          `${API_BASE}/library/personas/${id}/regenerate-story`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ language }),
          },
        );
        if (res.ok) {
          const updated = await res.json();
          await fetchPersonas();
          return updated;
        }
      } finally {
        setIsAiWorking(false);
      }
    },
    [language, fetchPersonas],
  );

  const generatePersona = useCallback(
    async (description = "") => {
      setIsAiWorking(true);
      try {
        const res = await fetch(`${API_BASE}/library/personas/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ description, language }),
        });
        if (res.ok) {
          const data = await res.json();
          await fetchPersonas();
          return data.persona || data;
        }
      } finally {
        setIsAiWorking(false);
      }
    },
    [language, fetchPersonas],
  );

  const generateFromArchetype = useCallback(
    async (archetypeId) => {
      setIsAiWorking(true);
      try {
        const res = await fetch(`${API_BASE}/library/personas/from-archetype`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ archetype_id: archetypeId, language }),
        });
        if (res.ok) {
          const data = await res.json();
          await fetchPersonas();
          return data.persona || data;
        }
      } finally {
        setIsAiWorking(false);
      }
    },
    [language, fetchPersonas],
  );

  // Panel operations
  const addToPanel = useCallback((personaId) => {
    setCurrentPanel((prev) => ({
      ...prev,
      persona_ids: prev.persona_ids.includes(personaId)
        ? prev.persona_ids
        : [...prev.persona_ids, personaId],
    }));
  }, []);

  const removeFromPanel = useCallback((personaId) => {
    setCurrentPanel((prev) => ({
      ...prev,
      persona_ids: prev.persona_ids.filter((id) => id !== personaId),
    }));
  }, []);

  const analyzePanel = useCallback(
    async (personaIds = null) => {
      const ids = personaIds || currentPanel.persona_ids;
      if (ids.length === 0) return;
      try {
        const res = await fetch(`${API_BASE}/library/panels/analyze`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            persona_ids: ids,
            benchmark_id: selectedBenchmark,
          }),
        });
        if (res.ok) setRepresentativeness(await res.json());
      } catch (e) {
        console.error(e);
      }
    },
    [currentPanel.persona_ids, selectedBenchmark],
  );

  // Re-analyze when panel changes
  useEffect(() => {
    const ids = currentPanel.persona_ids || [];
    if (ids.length > 0) {
      analyzePanel(ids);
    } else {
      setRepresentativeness(null);
    }
  }, [currentPanel.persona_ids, selectedBenchmark, analyzePanel]);

  const savePanel = useCallback(
    async (name, description = "") => {
      const res = await fetch(`${API_BASE}/library/panels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          description,
          persona_ids: currentPanel.persona_ids,
        }),
      });
      if (res.ok) {
        const saved = await res.json();
        const panelsRes = await fetch(`${API_BASE}/library/panels`);
        if (panelsRes.ok) {
          const d = await panelsRes.json();
          setPanels(Array.isArray(d) ? d : d.panels || []);
        }
        return saved;
      }
    },
    [currentPanel.persona_ids],
  );

  const loadPanel = useCallback(async (panelId) => {
    const res = await fetch(`${API_BASE}/library/panels/${panelId}`);
    if (res.ok) {
      const panel = await res.json();
      setCurrentPanel(panel);
    }
  }, []);

  const deletePanel_fn = useCallback(async (panelId) => {
    await fetch(`${API_BASE}/library/panels/${panelId}`, { method: "DELETE" });
    const panelsRes = await fetch(`${API_BASE}/library/panels`);
    if (panelsRes.ok) {
      const d = await panelsRes.json();
      setPanels(Array.isArray(d) ? d : d.panels || []);
    }
  }, []);

  const fillGaps = useCallback(async () => {
    setIsAiWorking(true);
    try {
      const res = await fetch(`${API_BASE}/library/personas/fill-gaps`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          persona_ids: currentPanel.persona_ids,
          benchmark_id: selectedBenchmark,
          language,
          count: 2,
        }),
      });
      if (res.ok) {
        const suggestions = await res.json();
        await fetchPersonas();
        return suggestions;
      }
    } finally {
      setIsAiWorking(false);
    }
  }, [currentPanel.persona_ids, selectedBenchmark, language, fetchPersonas]);

  // Editor helpers
  const openEditor = useCallback((persona = null) => {
    setEditingPersona(persona);
    setIsEditorOpen(true);
    setSubView("editor");
  }, []);

  const closeEditor = useCallback(() => {
    setEditingPersona(null);
    setIsEditorOpen(false);
    setSubView("library");
  }, []);

  // Get panel personas (full data)
  const panelIds = currentPanel.persona_ids || [];
  const panelPersonas = libraryPersonas.filter((p) => panelIds.includes(p.id));

  // Get all unique tags from library
  const allTags = [...new Set(libraryPersonas.flatMap((p) => p.tags || []))];

  return {
    // Library
    libraryPersonas,
    isLoading,
    searchQuery,
    setSearchQuery,
    tagFilter,
    setTagFilter,
    sortBy,
    setSortBy,
    allTags,
    fetchPersonas,
    createPersona,
    updatePersona,
    deletePersona,
    duplicatePersona,
    // AI
    isAiWorking,
    enrichPersona,
    regenerateStory,
    generatePersona,
    generateFromArchetype,
    // Editor
    editingPersona,
    setEditingPersona,
    isEditorOpen,
    openEditor,
    closeEditor,
    // Panel
    panels,
    currentPanel,
    setCurrentPanel,
    panelPersonas,
    addToPanel,
    removeFromPanel,
    analyzePanel,
    savePanel,
    loadPanel,
    deletePanel: deletePanel_fn,
    fillGaps,
    representativeness,
    // Benchmarks & archetypes
    benchmarks,
    archetypes,
    selectedBenchmark,
    setSelectedBenchmark,
    // Navigation
    subView,
    setSubView,
  };
}
